import React from "react";
import { FaEnvelope, FaSearch, FaTrash } from "react-icons/fa";
import getImageUrl from "../../../utils/imageUtils";
import styles from "../../../styles/components/profile/MessagesPanel.module.css";
import { getMessagePreviewText } from "../../../utils/parseSharedPostMessage";
import {
  formatConversationTime,
  getLatestSystemMessage,
  PRIORITY_LABELS,
} from "./messageUtils";

const ConversationList = ({
  title,
  searchQuery,
  onSearchChange,
  conversations,
  systemMessages,
  systemMessagesUnread,
  systemThreadOpen,
  selectedConversationId,
  isMarketplaceScope,
  showSystemItem,
  onOpenSystemThread,
  onSelectConversation,
  onDeleteConversation,
}) => {
  const latestSystemMessage = getLatestSystemMessage(systemMessages);
  const hasListItems = showSystemItem || conversations.length > 0;

  return (
    <div className={styles.conversationsList}>
      <div className={styles.conversationsHeader}>
        <h2 className={styles.conversationsHeaderTitle}>
          <FaEnvelope className={styles.conversationsHeaderIcon} aria-hidden />
          <span className={styles.conversationsHeaderText}>{title}</span>
        </h2>
        <div className={styles.searchBox}>
          <FaSearch className={styles.searchIcon} aria-hidden="true" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search conversations..."
            className={styles.searchInput}
            aria-label="Search conversations"
          />
        </div>
      </div>

      <div className={styles.conversations}>
        {showSystemItem && (
          <div
            className={`${styles.conversationItem} ${styles.systemConversationItem} ${
              systemThreadOpen ? styles.active : ""
            }`}
            onClick={onOpenSystemThread}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                onOpenSystemThread();
              }
            }}
          >
            <div
              className={`${styles.conversationAvatarPlaceholder} ${styles.systemAvatar}`}
            >
              <img src="/logo192.png" alt="Simpary" />
            </div>
            <div className={styles.conversationInfo}>
              <div className={styles.conversationHeader}>
                <span className={styles.conversationName}>System</span>
                <div className={styles.conversationHeaderRight}>
                  {latestSystemMessage?.priority &&
                    latestSystemMessage.priority !== "low" && (
                      <span
                        className={`${styles.priorityBadge} ${
                          {
                            urgent: styles.priority_urgent,
                            high: styles.priority_high,
                            medium: styles.priority_medium,
                          }[latestSystemMessage.priority] || ""
                        }`}
                      >
                        {PRIORITY_LABELS[latestSystemMessage.priority] ||
                          latestSystemMessage.priority}
                      </span>
                    )}
                  {systemMessagesUnread > 0 && (
                    <span className={styles.unreadBadge}>
                      {systemMessagesUnread}
                    </span>
                  )}
                  {systemMessages[0]?.createdAt && (
                    <span className={styles.conversationTime}>
                      {formatConversationTime(systemMessages[0].createdAt)}
                    </span>
                  )}
                </div>
              </div>
              {latestSystemMessage?.subject && (
                <p className={styles.lastMessage}>
                  {latestSystemMessage.subject}
                </p>
              )}
            </div>
          </div>
        )}

        {conversations.map((conv) => (
          <div
            key={conv._id}
            className={`${styles.conversationItem} ${
              selectedConversationId === conv._id ? styles.active : ""
            }`}
            onClick={() => onSelectConversation(conv)}
          >
            {conv.otherUser?.avatar ? (
              <img
                src={getImageUrl(conv.otherUser.avatar)}
                alt={conv.otherUser.firstName}
                className={styles.conversationAvatar}
              />
            ) : (
              <div className={styles.conversationAvatarPlaceholder}>
                <img src="/logo192.png" alt="User" />
              </div>
            )}
            <div className={styles.conversationInfo}>
              <div className={styles.conversationHeader}>
                <span className={styles.conversationName}>
                  {conv.otherUser?.firstName} {conv.otherUser?.lastName}
                </span>
                <div className={styles.conversationHeaderRight}>
                  {conv.unreadCount > 0 && (
                    <span className={styles.unreadBadge}>
                      {conv.unreadCount}
                    </span>
                  )}
                  {conv.lastMessageAt && (
                    <span className={styles.conversationTime}>
                      {formatConversationTime(conv.lastMessageAt)}
                    </span>
                  )}
                  <button
                    type="button"
                    className={styles.deleteConversationButton}
                    onClick={(e) => onDeleteConversation(conv._id, e)}
                    title="Delete conversation"
                    aria-label="Delete conversation"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
              {conv.lastMessage && (
                <>
                  {conv.listing?.title && (
                    <p className={styles.listingBadge}>
                      Listing: {conv.listing.title}
                    </p>
                  )}
                  <p className={styles.lastMessage}>
                    {getMessagePreviewText(conv.lastMessage.text, 50)}
                  </p>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {!hasListItems && (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <FaEnvelope size={64} />
          </div>
          <p>No conversations yet</p>
          <p className={styles.emptyStateSubtext}>
            {isMarketplaceScope
              ? "Message a seller from a listing to start chatting"
              : "Start a conversation with someone"}
          </p>
        </div>
      )}

      {hasListItems && conversations.length === 0 && searchQuery.trim() && (
        <div className={styles.searchEmptyState}>
          <p>No conversations match your search.</p>
        </div>
      )}
    </div>
  );
};

export default ConversationList;
